import React from 'react';

import DateRangeIcon from '@mui/icons-material/DateRange';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import { Box, Stack, Typography } from '@mui/material';

import { ControlledStaticDatePicker } from '@/components/NewUI/Components/CustomUIElements/SingleDatePicker';
import { Button } from '@/components/NewUI/ComponentsLibrary';
import dayjs from '@/components/NewUI/utils/dayjs';

import MondayFirstWeeks from './MondayFirstWeeks';
import { dayKeyLabel, formatDay, formatTime, SLOT_PERIODS } from './scheduling';
import { styles } from './styles';
import { aiButtonSx } from '../aiStyles';

import type { ISlot } from './types';

interface IProps {
  slotsByDay: Map<string, ISlot[]>;
  selectedDay: string;
  onSelectDay: (dayKey: string) => void;
  selectedSlot: string | null;
  onSelectSlot: (iso: string) => void;
  timeZone: string;
  unavailable: Set<string>;
  errorSlot: string | null;
  showLaterDates: boolean;
  canShowLater: boolean;
  onShowLater: () => void;
}

const SlotPicker = ({
  slotsByDay,
  selectedDay,
  onSelectDay,
  selectedSlot,
  onSelectSlot,
  timeZone,
  unavailable,
  errorSlot,
  showLaterDates,
  canShowLater,
  onShowLater
}: IProps) => {
  const dayKeys = [...slotsByDay.keys()];
  // The last day with a bookable slot in the chosen zone, not the raw cut-off.
  const lastDay = dayjs(dayKeys[dayKeys.length - 1]).format('ddd D MMM');
  const daySlots = slotsByDay.get(selectedDay) ?? [];

  return (
    <Box sx={styles.pickerRow}>
      <Stack sx={styles.calendarColumn}>
        <Box sx={styles.calendar}>
          {/* Calendar days are plain local dates ("system") so the day keys built
              in the candidate's chosen zone map 1:1 onto the grid. Days outside
              the month show, so Mon 2 and Tue 3 Nov are bookable without paging. */}
          <MondayFirstWeeks>
            <ControlledStaticDatePicker
              disableHighlightToday
              showDaysOutsideCurrentMonth
              displayStaticWrapperAs="desktop"
              fixedWeekNumber={6}
              maxDate={dayjs(dayKeys[dayKeys.length - 1])}
              minDate={dayjs(dayKeys[0])}
              onChange={(value) => (value ? onSelectDay(value.format('YYYY-MM-DD')) : undefined)}
              shouldDisableDate={(day) => !slotsByDay.has(day.format('YYYY-MM-DD'))}
              timezone="system"
              value={selectedDay}
              views={['day']}
            />
          </MondayFirstWeeks>
        </Box>
        <Stack sx={styles.laterDates}>
          <Typography sx={styles.caption}>
            {showLaterDates
              ? `Showing every date you can book, up to ${lastDay}.`
              : `Showing dates up to ${lastDay}.`}
          </Typography>
          {canShowLater && !showLaterDates ? (
            <Button
              buttonColor="purple"
              buttonVariant="text"
              icon={<DateRangeIcon />}
              id="booking-show-later-dates"
              onClick={onShowLater}
              size="small"
              sx={[styles.showLaterButton, aiButtonSx]}
            >
              Show later dates
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <Stack aria-labelledby="booking-times-heading" component="section" sx={styles.timesColumn}>
        <Typography component="h3" id="booking-times-heading" sx={styles.timesHeading}>
          {dayKeyLabel(selectedDay)}
        </Typography>

        {errorSlot ? (
          <Stack role="alert" sx={styles.notice}>
            <ErrorOutlineIcon aria-hidden />
            <Typography sx={styles.noticeText}>
              {`Sorry, ${formatTime(errorSlot, timeZone)} on ${formatDay(errorSlot, timeZone)} is no longer available. Please choose another time.`}
            </Typography>
          </Stack>
        ) : null}

        {SLOT_PERIODS.map((period) => {
          const periodSlots = daySlots.filter(
            (slot) => slot.minutes >= period.from && slot.minutes < period.to
          );
          if (periodSlots.length === 0) return null;
          return (
            <Stack
              key={period.label}
              aria-label={`${period.label} times`}
              role="group"
              sx={styles.periodGroup}
            >
              <Typography aria-hidden sx={styles.periodLabel}>
                {period.label}
              </Typography>
              <Box sx={styles.slotGrid}>
                {periodSlots.map((slot) => {
                  const taken = unavailable.has(slot.iso);
                  const selected = selectedSlot === slot.iso;
                  const time = formatTime(slot.iso, timeZone);
                  return (
                    <Button
                      key={slot.iso}
                      aria-label={taken ? `${time}, no longer available` : time}
                      aria-pressed={selected}
                      buttonColor="purple"
                      buttonVariant={selected ? 'primary' : 'outline'}
                      disabled={taken}
                      id={`booking-slot-${dayjs(slot.iso).tz(timeZone).format('YYYYMMDD-HHmm')}`}
                      onClick={() => onSelectSlot(slot.iso)}
                      sx={[styles.slotButton, aiButtonSx, taken ? styles.unavailableSlot : {}]}
                    >
                      {time}
                    </Button>
                  );
                })}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

export default SlotPicker;
